// OrderCheckoutDrawer.jsx
import { useEffect, useState, useMemo, useRef, useCallback, useContext } from "react";
import {
  FaTimes,
  FaCheckCircle,
  FaFileInvoice,
  FaEnvelope,
  FaPhone,
  FaMapPin,
  FaChartBar,
  FaClock,
  FaRupeeSign,
  FaClipboardList,
  FaHistory,
  FaReceipt,
  FaCalendar,
  FaUndo
} from "react-icons/fa";
import {
  GiSittingDog,
  GiCat,
  GiCow,
  GiGoat,
  GiRabbit,
  GiSquirrel,
  GiTortoise,
  GiHummingbird,
} from "react-icons/gi";
import { httpService } from "../../services/httpService";
import BoardingCheckoutCard from "../Checkout/BoardingCheckoutCard";
import GroomingCheckoutCard from "../Checkout/GroomingCheckoutCard";
import VetCheckoutCard from "../Checkout/VetCheckoutCard";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import OrderFormsSection from "../common/OrderFormsSection";
import OrderPaymentDrawer from "./OrderPaymentDrawer";
import { TokenContext } from "../../context/TokenContext";
import { IoBagCheckOutline } from "react-icons/io5";
import BoardingStartedDrawer from "../../pages/services/BoardingStartedDrawer";
import GroomingStartedDrawer from "../../pages/services/GroomingStartedDrawer";
import VetClinicStartedDrawer from "../../pages/services/VetClinicStartedDrawer";
import ReturnProductDrawer from "./ReturnProductDrawer";
import ReturnHistorySection from "./ReturnHistorySection";
import { useProfile } from "../../context/ProfileContext";
import AdjustmentAmountCard from "../common/AdjustmentAmountCard";

// ---------- Pet icon helper (inline) ----------
const getPetIcon = (petType) => {
  switch ((petType || "").toLowerCase()) {
    case "dog":
      return <GiSittingDog className="w-5 h-5 text-amber-600" />;
    case "cat":
      return <GiCat className="w-5 h-5 text-gray-600" />;
    case "cow":
      return <GiCow className="w-5 h-5 text-gray-700" />;
    case "goat":
      return <GiGoat className="w-5 h-5 text-gray-700" />;
    case "rabbit":
      return <GiRabbit className="w-5 h-5 text-pink-500" />;
    case "squirrel":
      return <GiSquirrel className="w-5 h-5 text-orange-600" />;
    case "tortoise":
    case "turtle":
      return <GiTortoise className="w-5 h-5 text-green-600" />;
    case "bird":
    case "parrot":
      return <GiHummingbird className="w-5 h-5 text-blue-500" />;
    default:
      return <GiSittingDog className="w-5 h-5 text-gray-500" />;
  }
};

// ---------- Mapping function: API response -> component shape ----------
const mapApiOrderToComponent = (apiOrder) => {
  if (!apiOrder) return null;

  // Deduplicate pets from order_items
  const petsMap = new Map();
  (apiOrder.order_items || []).forEach((item) => {
    if (item.vendor_pet_id && !petsMap.has(item.vendor_pet_id)) {
      petsMap.set(item.vendor_pet_id, {
        id: item.vendor_pet_id,
        name: item.pet_name || "Unknown",
        petType: item.pet_type || "dog",
        breed: item.pet_breed || "",
        size: item.pet_size || "",
        weight: item.weight_kg ? `${item.weight_kg} kg` : "",
        pet_type_id: item?.pet_type_id,
      });
    }
  });
  const pets = Array.from(petsMap.values());

  // Map order_items to services
  const services = (apiOrder.order_items || []).map((item) => ({
    service_id: item.service_id,
    petId: item.vendor_pet_id || "",
    selectedServiceId: item.service_id || "",
    type: item.item_type === "vet_clinic" ? "vet" : item.item_type,
    serviceName: item.service_name || "Service",
    checkin: item.start_date || null,
    checkout: item.end_date || item.start_date || null,
    serviceDate: item.start_date || null,
    price: item?.price || item?.amount_with_tax || 0,
    base_total: item?.base_total || item?.price || 0,
    final_total: item.final_total ?? item.amount_with_tax ?? 0,
    addons: item.addons || [],
    products: item.products || [],
    otherCharges: item.otherCharges || [],
    service_based_billing: item.service_based_billing || {},
    service_based_billing_total: item.service_based_billing_total || 0,
    ref_id: item.ref_id || "",
    quantity: item.quantity || 1,
    room_type_id: item.room_type_id || "",
    room_id: item.room_id || "",
    room_number: item.room_number || "",
    roomType: item.room_name || "",
    unit_price: item.unit_price || item.amount_without_tax || 0,
    tax_percentage: item.tax_percentage || 0,
    gst_amount: item.gst || 0,
    discount_amount: item.discount_amount || 0,
    surchargeDetails: item.surcharge_details || [],
    surchargesTotal:
      item.surcharge_details?.reduce((sum, s) => sum + (s.price || 0), 0) || 0,
    amount_before_discount: item.amount_before_discount || item.base_total || 0,
    mrp_price: item.mrp_price || 0,
    status: item.status,
  }));

  const adjustmentAmount = Number(apiOrder.adjustment_amount || 0);
  const total = Number(apiOrder.grand_total || 0);
  const paid = Number(apiOrder.total_paid_amount || 0);

  // final total after adjustment (prefer backend value if present)
  let finalTotal = Number(apiOrder.amount_after_adjustment);
  if (!Number.isFinite(finalTotal) || finalTotal === 0) {
    finalTotal = adjustmentAmount > 0 ? total - adjustmentAmount : total;
  }
  finalTotal = Math.max(finalTotal, 0);

  return {
    orderNumber: apiOrder.order_number || "",
    invoiceNumber: apiOrder.invoice_history?.[0]?.invoice_number || "",
    status: apiOrder.status || "pending",
    client: {
      name: `${apiOrder.client_first_name || ""} ${apiOrder.client_last_name || ""}`.trim(),
      phone: apiOrder.client_phone || "",
      email: apiOrder.client_email || "",
      address: apiOrder.client_address || "",
      notes: apiOrder.notes || "",
      client_id: apiOrder.vendor_client_id || "",
    },
    payment: {
      advanceReceived: paid.toFixed(0),
      pendingAmount: (finalTotal - paid).toFixed(0),
      total: finalTotal,
      grandTotal: total,                                   // ✅ pre-adjustment total
      adjustmentAmount,
      adjustmentReason: apiOrder.adjustment_reason || "",  // ✅ persisted reason
      amountAfterAdjustment: finalTotal,
    },
    discount: apiOrder.total_discount || 0,
    recentVisits: [],
    pets,
    services,
    adjustmentAmount,
    apiTotals: {
      subtotal: apiOrder.subtotal || 0,
      discount: apiOrder.total_discount || 0,
      net: (apiOrder.subtotal || 0) - (apiOrder.total_discount || 0),
      tax: apiOrder.tax_tax || apiOrder.tax_total || 0,
      total: total.toFixed(0),
      balance: Number(Math.max(finalTotal - paid, 0).toFixed(0)),
    },
  };
};

const OrderCheckoutDrawer = ({ isOpen, onClose, orderId, hideInvoiceSplit = false }) => {
  const [order, setOrder] = useState(null);
  const [rawOrderData, setRawOrderData] = useState(null);
  const [originalOrder, setOriginalOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeClientPanel, setActiveClientPanel] = useState(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [taxPercent] = useState(5);
  const [formData, setFormData] = useState({ visitDate: "", notes: "" });

  const [isRecalculating, setIsRecalculating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const recalcTimer = useRef(null);
  const [showPaymentDrawer, setShowPaymentDrawer] = useState(false);
  const [boardingStartedDrawerOpen, setBoardingStartedDrawerOpen] = useState(false);
  const [selectedBoardingBooking, setSelectedBoardingBooking] = useState(null);
  const [selectedBoardingRow, setSelectedBoardingRow] = useState(null);
  const [groomingStartedDrawerOpen, setGroomingStartedDrawerOpen] = useState(false);
  const [selectedGroomingBooking, setSelectedGroomingBooking] = useState(null);
  const [selectedGroomingRow, setSelectedGroomingRow] = useState(null);
  const [vetStartedDrawerOpen, setVetStartedDrawerOpen] = useState(false);
  const [selectedVetBooking, setSelectedVetBooking] = useState(null);
  const [selectedVetRow, setSelectedVetRow] = useState(null);
  const { decodedData } = useContext(TokenContext);
   const { profileData, fetchProfile } = useProfile();
   const systemSettings = profileData?.systemSettings;
  const [isOrderClosed, setIsOrderClosed] = useState(false);
  const [recentVisits, setRecentVisits] = useState([]);

  const [splitOrderId, setSplitOrderId] = useState(null);
  const [showSplitOrderDrawer, setShowSplitOrderDrawer] = useState(false);
  const [selectedForInvoice, setSelectedForInvoice] = useState(new Set());
  const [returnHistory, setReturnHistory] = useState([]);
  const [returnableRaw, setReturnableRaw] = useState([]);

  // ---------- Adjustment state ----------
  const [adjustmentAmount, setAdjustmentAmount] = useState(0);
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [savingAdjustment, setSavingAdjustment] = useState(false);

  // Sync adjustment inputs whenever order loads / reloads
  useEffect(() => {
    if (order?.payment) {
      setAdjustmentAmount(Number(order.payment.adjustmentAmount) || 0);
      setAdjustmentReason(order.payment.adjustmentReason || "");
    }
  }, [order?.payment?.adjustmentAmount, order?.payment?.adjustmentReason]);

  const handleSaveAdjustment = async () => {
    if (!orderId || savingAdjustment) return;

    const numericAmount = parseFloat(adjustmentAmount) || 0;
    const originalAmount = parseFloat(order?.payment?.grandTotal || 0);
    const currentAdjustment = parseFloat(order?.payment?.adjustmentAmount || 0);
    const currentReason = order?.payment?.adjustmentReason || "";

    if (numericAmount < 0) {
      toast.error("Adjustment cannot be negative");
      return;
    }
    if (numericAmount > originalAmount) {
      toast.error(
        `Adjustment cannot exceed order amount (₹${originalAmount.toFixed(0)})`
      );
      return;
    }
    if (numericAmount === currentAdjustment && adjustmentReason === currentReason) {
      return;
    }

    try {
      setSavingAdjustment(true);
      const res = await httpService.post("/orderpayment/saveOrderAdjustment", {
        order_id: orderId,
        adjustment_amount: numericAmount,
        adjustment_reason: adjustmentReason,
      });

      if (res?.status) {
        toast.success("Adjustment saved successfully!", { position: "top-center" });
        await fetchOrder();
      } else {
        throw new Error(res?.message || "Failed to save adjustment");
      }
    } catch (err) {
      console.error("Save adjustment error:", err);
      toast.error(
        err?.response?.data?.message || "Failed to save adjustment",
        { position: "top-center" }
      );
      setAdjustmentAmount(currentAdjustment);
      setAdjustmentReason(currentReason);
    } finally {
      setSavingAdjustment(false);
    }
  };

  const splitIdemRef = useRef({ fp: null, key: null });
  const splitInFlightRef = useRef(false);
  const profileFetchedRef = useRef(false);

  const fetchReturnableProducts = async (id) => {
  if (!id) return;
  try {
    const res = await httpService.get(`/vendororder/returnable-products/${id}`);
    const payload = res?.data;
    setReturnableRaw(Array.isArray(payload) ? payload : payload?.products || []);
  } catch (err) {
    console.error("Error fetching returnable products:", err);
    setReturnableRaw([]);
  }
};
  // New state for client overview
  const [clientOverviewData, setClientOverviewData] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showReturnDrawer, setShowReturnDrawer] = useState(false);
  const handleCancelservice = async (service) => {
    if (isOrderClosed) return;
    const status = String(service?.status || "").toLowerCase().trim();
    if (
      ["closed", "checkout", "checked_out", "cancelled", "canceled", "no show"].includes(
        status,
      )
    ) {
      return;
    }

    if (isOrderClosed) return;
    const serviceStatus = String(service?.status || "").toLowerCase().trim();
    if (
      ["closed", "checkout", "checked_out", "cancelled", "canceled", "no show"].includes(
        serviceStatus,
      )
    ) {
      return;
    }

    const result = await Swal.fire({
      title: "Cancel Booking?",
      text: "This will cancel the Booking in this order. Continue?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Cancel",
      cancelButtonText: "No",
    });
    if (!result.isConfirmed) return;

    try {
      if (service.type === "boarding") {
        await httpService.put(
          `/vendorbooking/updateVendorBookingStatus/${service.ref_id}`,
          { status: "cancelled" },
        );
      } else {
        await httpService.put(
          `/vendorappointment/updateVendorAppointmentStatus/${service.ref_id}`,
          { status: "cancelled" },
        );
      }
      toast.success("Booking cancelled successfully.");
      fetchOrder();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to cancel order.");
    }
  };

  const fetchRecentVisits = async (clientId) => {
    if (!clientId) return;

    try {
      const res = await httpService.get(
        `/vendorbooking/bookingHistory/${clientId}`,
      );

      if (res.status && Array.isArray(res.data)) {
        setRecentVisits(res.data.filter(item=> item._id != orderId));
      } else {
        setRecentVisits([]);
      }
    } catch (err) {
      console.error(err);
      setRecentVisits([]);
    }
  };

  const fetchOrder = async () => {
    try {
      const orderResponse = await httpService.get(
        `/vendororder/getvendororder/${orderId}`,
      );
      if (orderResponse.code === 200 && orderResponse.data) {
        const orderData = orderResponse.data;
        const mappedOrder = mapApiOrderToComponent(orderData);

        setRawOrderData(orderData);
        setOrder(mappedOrder);
        setOriginalOrder(structuredClone(mappedOrder));
        const orderStatus = String(orderData?.status || "")
          .toLowerCase()
          .trim();
        setIsOrderClosed(orderStatus === "closed");
        fetchRecentVisits(orderData.vendor_client_id);
        fetchReturnHistory(orderId);
        if (orderStatus === "closed") fetchReturnableProducts(orderId);
        else setReturnableRaw([]);
      } else {
        setError("Failed to load order data.");
      }
    } catch (err) {
      console.error("Error fetching order:", err);
      setError("Error fetching order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

   const fetchReturnHistory = async (id) => {
      if (!id) return;
      try {
        const res = await httpService.get(`/vendororder/order-refunds/${id}`);
        console.log("order-refunds response:", res); // remove once working
        const list = res?.data?.data || res?.data || [];
        setReturnHistory(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Error fetching refund history:", err);
        setReturnHistory([]);
      }
    };

  // Fetch client overview
  const fetchClientOverview = async () => {
    if (!order?.client?.client_id) return;

    try {
      setLoadingStats(true);
      const response = await httpService.get(
        `/vendorclient/getVendorClientPetsOverviewById/${order.client.client_id}`,
      );
      if (response.code === 200) {
        setClientOverviewData(response.data?.data || response.data);
      }
    } catch (error) {
      console.error("Error fetching client overview:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  const calculateStats = () => {
    if (!clientOverviewData) {
      return {
        paidAmount: 0,
        pendingAmount: 0,
        totalOrders: 0,
        cancellations: 0,
      };
    }

    const overviewData = clientOverviewData?.data || clientOverviewData;
    const paymentDetails = overviewData.payment_details || {};

    const paidAmount =
      paymentDetails.received?.reduce(
        (sum, payment) => sum + (parseFloat(payment.payment_amount) || 0),
        0,
      ) || 0;

    const pendingRaw = paymentDetails.pending || [];
    const received = paymentDetails.received || [];
    const filteredPending = pendingRaw.filter((pending) => {
      const orderTotal = pending.order_amount;
      const receivedForOrder = received.filter(
        (p) =>
          p.order_number === pending.order_number ||
          p.order_id === pending.order_id,
      );
      const totalReceived = receivedForOrder.reduce(
        (sum, p) => sum + (p.payment_amount || 0),
        0,
      );
      return totalReceived < orderTotal - 0.01;
    });
    const pendingAmount = filteredPending.reduce(
      (sum, p) => sum + (p.balance || 0),
      0,
    );

    const totalOrders = overviewData.order_details?.length || 0;

    let totalCancellations = 0;

    totalCancellations +=
      overviewData.order_details?.filter((order) => {
        const status = order.status?.toLowerCase();
        return status === "cancel" || status === "cancelled";
      }).length || 0;

    totalCancellations +=
      overviewData.upcoming_appointment_details?.filter((appointment) => {
        const appointmentStatus = appointment.status?.toLowerCase();
        if (appointmentStatus === "cancel" || appointmentStatus === "cancelled")
          return true;
        return appointment.items?.some((item) => {
          const serviceInfo = item.appointment_info || item.boarding_info;
          return ["cancel", "cancelled"].includes(
            serviceInfo?.status?.toLowerCase(),
          );
        });
      }).length || 0;

    totalCancellations +=
      overviewData.completed_cancelled_appointments?.filter((appointment) => {
        const appointmentStatus = appointment.status?.toLowerCase();
        if (appointmentStatus === "cancel" || appointmentStatus === "cancelled")
          return true;
        return appointment.items?.some((item) => {
          const serviceInfo = item.appointment_info || item.boarding_info;
          const itemStatus = serviceInfo?.status?.toLowerCase();
          return ["cancel", "cancelled"].includes(itemStatus);
        });
      }).length || 0;

    return {
      paidAmount,
      pendingAmount,
      totalOrders,
      cancellations: totalCancellations,
    };
  };

  const handleOpenBoardingStarted = (service) => {
    const booking = {
      order_id: orderId,

      // Boarding booking reference
      booking_id: service?.ref_id || "",
      start_date: service?.checkin || "",
      end_date: service?.checkout || "",

      status: service?.status || "confirmed",
      pet: {
        vendor_pet_id: service?.petId || "",
        vendor_client_id: service?.vendor_client_id || "",
      },
      service: {
        service_ref_id: service?.ref_id || "",
        service_name: service?.serviceName || "Boarding",
        start_date: service?.checkin || "",
        end_date: service?.checkout || "",
        start_time: service?.start_time || "",
        duration: service?.duration || "",
        total_price: service?.price || service?.total_price || 0,
      },
    };

    setSelectedBoardingBooking(booking);

    setSelectedBoardingRow({
      ...service,

      ref_id: service?.ref_id || "",
      service_ref_id: service?.ref_id || "",
      start_date: service?.checkin || "",
      end_date: service?.checkout || "",
      vendor_pet_id: service?.petId || "",
      order_id: orderId,
    });

    setBoardingStartedDrawerOpen(true);
  };

  const handleCloseBoardingStarted = () => {
    setBoardingStartedDrawerOpen(false);
    setSelectedBoardingBooking(null);
    setSelectedBoardingRow(null);
  };

  const handleOpenGroomingStarted = (service) => {
    const originalItem =
      rawOrderData?.order_items?.find(
        (item) => item.ref_id === service?.ref_id,
      ) || {};

    const pet = getPetInfo(service?.petId);

    const booking = {
      order_id: orderId,

      booking_id:
        originalItem?.ref_id || service?.ref_id || originalItem?._id || "",

      status: originalItem?.status || service?.status || "upcoming",

      customer: {
        name:
          `${rawOrderData?.client_first_name || ""} ${
            rawOrderData?.client_last_name || ""
          }`.trim() ||
          order?.client?.name ||
          "",

        phone: rawOrderData?.client_phone || order?.client?.phone || "",

        email: rawOrderData?.client_email || order?.client?.email || "",
      },

      pet: {
        vendor_pet_id: originalItem?.vendor_pet_id || service?.petId || "",

        vendor_client_id:
          originalItem?.vendor_client_id ||
          rawOrderData?.vendor_client_id ||
          "",

        name: originalItem?.pet_name || pet?.name || "",

        breed: originalItem?.pet_breed || pet?.breed || "",

        size: originalItem?.pet_size || pet?.size || "",

        type: originalItem?.pet_type || pet?.petType || "",
      },

      service: {
        service_name:
          originalItem?.service_name || service?.serviceName || "Grooming",

        duration: originalItem?.duration || service?.duration || "",

        start_date: originalItem?.start_date || service?.checkin || "",

        start_time: originalItem?.start_time || service?.startTime || "",

        staffName: originalItem?.employee_name || service?.employeeName || "",

        total_price:
          originalItem?.total_price ||
          originalItem?.unit_price_with_tax ||
          originalItem?.price ||
          service?.price ||
          0,
      },

      pricing: {
        total:
          originalItem?.total_price ||
          originalItem?.unit_price_with_tax ||
          originalItem?.price ||
          service?.price ||
          0,
      },
    };

    setSelectedGroomingBooking(booking);
    setSelectedGroomingRow({
      ...originalItem,

      ref_id: originalItem?.ref_id || service?.ref_id || "",

      vendor_pet_id: originalItem?.vendor_pet_id || service?.petId || "",

      order_id: orderId,
    });

    setGroomingStartedDrawerOpen(true);
  };

  const handleCloseGroomingStarted = () => {
    setGroomingStartedDrawerOpen(false);
    setSelectedGroomingBooking(null);
    setSelectedGroomingRow(null);
  };

  const handleOpenVetStarted = (service) => {
    const originalItem =
      rawOrderData?.order_items?.find(
        (item) => item.ref_id === service?.ref_id,
      ) || {};

    const pet = getPetInfo(service?.petId);

    const booking = {
      order_id: orderId,

      booking_id:
        originalItem?.ref_id || service?.ref_id || originalItem?._id || "",

      status: originalItem?.status || service?.status || "upcoming",

      customer: {
        name:
          `${rawOrderData?.client_first_name || ""} ${
            rawOrderData?.client_last_name || ""
          }`.trim() ||
          order?.client?.name ||
          "",

        phone: rawOrderData?.client_phone || order?.client?.phone || "",

        email: rawOrderData?.client_email || order?.client?.email || "",
      },

      pet: {
        vendor_pet_id: originalItem?.vendor_pet_id || service?.petId || "",

        vendor_client_id:
          originalItem?.vendor_client_id ||
          rawOrderData?.vendor_client_id ||
          order?.client?.client_id ||
          "",

        name: originalItem?.pet_name || pet?.name || "",

        breed: originalItem?.pet_breed || pet?.breed || "",

        size: originalItem?.pet_size || pet?.size || "",

        type: originalItem?.pet_type || pet?.petType || "",
      },

      service: {
        service_name:
          originalItem?.service_name || service?.serviceName || "Vet",

        duration: originalItem?.duration || service?.duration || "",

        start_date:
          originalItem?.start_date ||
          service?.checkin ||
          service?.serviceDate ||
          "",

        start_time: originalItem?.start_time || service?.startTime || "",

        staffName:
          originalItem?.employee_name ||
          service?.employeeName ||
          service?.assignedStaff ||
          "",

        total_price:
          originalItem?.total_price ||
          originalItem?.unit_price_with_tax ||
          originalItem?.price ||
          service?.price ||
          0,
      },

      pricing: {
        total:
          originalItem?.total_price ||
          originalItem?.unit_price_with_tax ||
          originalItem?.price ||
          service?.price ||
          0,
      },
    };

    setSelectedVetBooking(booking);

    setSelectedVetRow({
      ...originalItem,

      ref_id: originalItem?.ref_id || service?.ref_id || "",

      vendor_pet_id: originalItem?.vendor_pet_id || service?.petId || "",

      vendor_client_id:
        originalItem?.vendor_client_id ||
        rawOrderData?.vendor_client_id ||
        order?.client?.client_id ||
        "",

      order_id: orderId,
    });

    setVetStartedDrawerOpen(true);
  };

  const handleCloseVetStarted = () => {
    setVetStartedDrawerOpen(false);
    setSelectedVetBooking(null);
    setSelectedVetRow(null);
  };

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    fetchOrder();
  }, [isOpen, orderId]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && order?.client?.client_id) {
      fetchClientOverview();
    }
  }, [isOpen, order?.client?.client_id]);

    useEffect(() => {
    if (isOpen && fetchProfile && !profileFetchedRef.current) {
      profileFetchedRef.current = true;
      fetchProfile().catch((err) =>
        console.error("Failed to refresh profile", err),
      );
    }
    if (!isOpen) {
      profileFetchedRef.current = false; // re-arm so next drawer-open fetches fresh
    }
  }, [isOpen, fetchProfile]);

  // Recalculation logic (accepts order as parameter)
  const recalculateOrder = useCallback(
    async (orderToRecalc) => {
      if (
        !orderToRecalc ||
        !orderToRecalc.services?.length ||
        !orderToRecalc.client?.client_id
      )
        return;

      const includedServices = []; // { serviceIndex } for mapping back to prevOrder.services
      const items = [];

      orderToRecalc.services.forEach((service, idx) => {
        // ✅ Skip cancelled services – do not send them to the API
        if (service.status === "cancelled") return;

        const originalItem = rawOrderData?.order_items?.find(
          (item) => item.ref_id === service.ref_id,
        );

        const isBoarding =
          service.type === "boarding" || service.type === "day_care";

        let quantity = service.quantity || 1;
        if (isBoarding && service.checkin && service.checkout) {
          const nights = Math.max(
            1,
            Math.ceil(
              (new Date(service.checkout) - new Date(service.checkin)) /
                86400000,
            ),
          );
          quantity = nights;
        }

        const info = {
          pet_details: {
            _id: service.petId,
            breed_info: { _id: originalItem?.pet_breed_id },
            size_info: { _id: originalItem?.pet_size_id },
          },
          ...(!isBoarding && {
            vendor_service_id:
              service.selectedServiceId || originalItem?.service_id,
          }),
          ...(isBoarding && {
            room_service_id:
              service.selectedServiceId || originalItem?.service_id,
          }),
          room_type_id:
            service.room_type_id || originalItem?.room_type_id || null,
          start_date: service.checkin,
          end_date: isBoarding ? service.checkout : null,
        };

        if (!isBoarding) {
          info.appointment_date = service.checkin;
        }

        items.push({
          type: service.type === "vet" ? "vet_clinic" : service.type,
          quantity,
          [isBoarding ? "boarding_info" : "appointment_info"]: info,
          products: service.products || [],
          addons: service.addons || [],
          otherCharges: service.otherCharges || [],
          service_based_billing:
            service.service_based_billing ||
            originalItem?.service_based_billing ||
            {},
        });

        // Remember which index in `prevOrder.services` this item belongs to
        includedServices.push({ serviceIndex: idx });
      });

      if (items.length === 0) return;

      const payload = {
        vendor_client_id: orderToRecalc.client.client_id,
        items,
        round_off: 0,
        waived_amount: 0,
      };

      setIsRecalculating(true);
      try {
        const response = await httpService.post(
          "/vendororder/recalculateOrder",
          payload,
        );
        const data = response.data;

        // Map each included service index -> its recalculated item
        const calculatedByIndex = new Map();
        includedServices.forEach((ref, i) => {
          calculatedByIndex.set(ref.serviceIndex, data.items[i]);
        });

        setOrder((prevOrder) => {
          const updatedServices = prevOrder.services.map((service, idx) => {
            const calculated = calculatedByIndex.get(idx);
            if (calculated) {
              return {
                ...service,
                price: calculated.total_price || service.price,
                unit_price: calculated.unit_price || service.unit_price,
                tax_percentage:
                  calculated.tax_percentage || service.tax_percentage,
                gst_amount: calculated.gst || service.gst_amount,
                discount_amount:
                  calculated.discount_amount || service.discount_amount,
                surchargeDetails: calculated.surcharge_details || [],
                surchargesTotal:
                  calculated.surcharge_details?.reduce(
                    (sum, s) => sum + (s.price || 0),
                    0,
                  ) || 0,
                amount_before_discount:
                  calculated.amount_before_discount || calculated.base_total || 0,
                base_total: calculated.base_total || 0,
              };
            }
            return service;
          });

          const adjustmentAmount = prevOrder.adjustmentAmount || 0;
          const grandTotal = Number(data.grand_total || 0);
          const paid = Number(prevOrder.payment.advanceReceived || 0);
          const finalTotal = Math.max(grandTotal - adjustmentAmount, 0);
          const balance = Math.max(finalTotal - paid, 0);

          return {
            ...prevOrder,
            services: updatedServices,
            adjustmentAmount,
            apiTotals: {
              subtotal: data.subtotal,
              discount: data.total_discount,
              net: (data.subtotal || 0) - (data.total_discount || 0),
              tax: data.tax_total,
              total: grandTotal.toFixed(0),
              balance: balance,
            },
            payment: {
              ...prevOrder.payment,
              grandTotal,                                    // ✅ keep in sync
              total: finalTotal.toFixed(0),
              pendingAmount: balance,
            },
          };
        });
      } catch (error) {
        console.error("Recalculation failed:", error);
        // toast.error("Failed to recalculate order totals");
      } finally {
        setIsRecalculating(false);
      }
    },
    [rawOrderData]
  );

  // Debounced scheduler
  const scheduleRecalculate = useCallback(
    (orderToRecalc) => {
      if (recalcTimer.current) clearTimeout(recalcTimer.current);
      recalcTimer.current = setTimeout(() => {
        recalculateOrder(orderToRecalc);
      }, 100);
    },
    [recalculateOrder]
  );

  // Service update helper (triggers recalculation on relevant changes)
  const handleUpdateService = (serviceRefId, updatedService) => {
    if (isOrderClosed) return;
    const existing = order?.services?.find((s) => s.ref_id === serviceRefId);
    const existingStatus = String(existing?.status || "")
      .toLowerCase()
      .trim();
    if (
      ["closed", "checkout", "checked_out", "cancelled", "canceled", "no show"].includes(
        existingStatus,
      )
    ) {
      return;
    }

    // Compute the new order based on the current order state
    const newOrder = {
      ...order,
      services: order.services.map((s) =>
        s.ref_id === serviceRefId ? { ...s, ...updatedService } : s,
      ),
    };
    // Update state with the new order
    setOrder(newOrder);

    // Now you can safely use `newOrder` for side effects (no delay)
    scheduleRecalculate(newOrder);
    handleSave(newOrder);   // or whatever your API call is
  };

  const toggleClientPanel = (panel) =>
    setActiveClientPanel((prev) => (prev === panel ? null : panel));
  const getPetInfo = (petId) => order?.pets?.find((p) => p.id === petId) || {};

  const calculateTotals = useMemo(() => {
    if (!order) {
      return { subtotal: 0, discount: 0, net: 0, tax: 0, total: 0, balance: 0, adjustment: 0, finalTotal: 0 };
    }

    // Calculate item-wise totals matching Create New Sale / TotalsSummary
    let calcSubtotal = 0;      // Sum of MRPs (tax inclusive before discount)
    let calcDiscount = 0;      // Sum of all discounts
    let calcTax = 0;           // Sum of all taxes
    let calcTotal = 0;         // Sum of final item totals

    (order.services || []).forEach((service) => {
      const serviceTotal = Number(service.final_total ?? service.price ?? 0);
      const serviceDiscount = Number(service.discount_amount || 0);
      const serviceTax = Number(service.gst_amount || 0);
      const serviceMrp = Number(
        service.amount_before_discount ||
          service.mrp_price ||
          (serviceTotal + serviceDiscount)
      );

      calcSubtotal += serviceMrp;
      calcDiscount += serviceDiscount;
      calcTax += serviceTax;
      calcTotal += serviceTotal;

      // Products
      (service.products || []).forEach((p) => {
        const qty = Number(p.quantity || 1);
        const mrp = Number(p.mrp_price || p.sale_price || p.mrp || p.unit_price || 0);
        const effective = Number(p.unit_price || p.effective_sale_price || p.sale_price_after_discount || 0);
        const totalMrp = (mrp || effective) * qty;
        const totalEffective = effective * qty;
        const discount = Math.max(0, totalMrp - totalEffective);
        const tax = Number(p.gst || p.gstAmount || p.tax_amount || 0) * qty;

        calcSubtotal += totalMrp;
        calcDiscount += discount;
        calcTax += tax;
        calcTotal += totalEffective;
      });

      // Addons
      (service.addons || []).forEach((a) => {
        const qty = Number(a.quantity || 1);
        const price = Number(a.addon_price || a.price || 0);
        const totalAddon = price * qty;
        const discount = Number(a.discount_amount || 0);
        const tax = Number(a.gst || a.tax_amount || 0);

        calcSubtotal += totalAddon;
        calcDiscount += discount;
        calcTax += tax;
        calcTotal += (totalAddon - discount);
      });

      // Other Charges
      (service.otherCharges || []).forEach((c) => {
        const amount = Number(c.amount || c.price || 0);
        const tax = Number(c.gst || c.tax || 0);

        calcSubtotal += amount;
        calcTax += tax;
        calcTotal += amount;
      });
    });

    const paid = Number(order.payment?.advanceReceived || 0);
    const adjustment = Number(order.adjustmentAmount || 0);

    const rawOrderTotal = calcTotal > 0 ? calcTotal : Number(order.apiTotals?.total || order.payment?.grandTotal || 0);
    const finalTotal = Number(
      order.payment?.amountAfterAdjustment ||
      (adjustment !== 0 ? Math.max(0, rawOrderTotal - adjustment) : rawOrderTotal)
    );

    const subtotal = calcSubtotal > 0 ? calcSubtotal : Number(order.apiTotals?.subtotal || 0);
    const discount = calcDiscount > 0 ? calcDiscount : Number(order.apiTotals?.discount || 0);
    const tax = calcTax > 0 ? calcTax : Number(order.apiTotals?.tax || 0);
    const total = rawOrderTotal;
    const balance = Math.max(finalTotal - paid, 0);

    // Net Amount = Amount without tax (as in Create New Sale / TotalsSummary)
    const net = Math.max(0, (subtotal - discount) - tax);

    return { subtotal, discount, net, tax, total, balance, adjustment, finalTotal };
  }, [order]);

  const selectedInvoiceTotals = useMemo(() => {
    if (!order || selectedForInvoice.size === 0) return null;
    const items = order.services.filter((s) => selectedForInvoice.has(s.ref_id));
    // service.price is already tax-inclusive (mapped from amount_with_tax)
    const total = items.reduce(
    (sum, s) => sum + Number(s.final_total ?? s.price ?? 0),
    0,
  );
  return { items, total };
  }, [order, selectedForInvoice]);

   const returnableProducts = useMemo(() => {
  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  return returnableRaw
    .map((p) => {
      const serviceRefId = p.order_item_ref_id;
      const productId = p.product_id;
      const service = order?.services?.find((s) => s.ref_id === serviceRefId);

      return {
        key: `${serviceRefId}_${productId}`,
        productId,
        name: p.name ?? p.raw?.product_name ?? "Product",
        unitPrice: toNum(p.raw?.final_price ?? p.raw?.unit_price ?? p.price),
        purchasedQty: toNum(p.ordered_quantity),
        returnedQty: toNum(p.returned_quantity),
        returnableQty: toNum(p.returnable_quantity),
        serviceRefId,
        serviceName: service?.serviceName || "",
        petId: service?.petId || "",
        petName: getPetInfo(service?.petId)?.name || "",
      };
    })
    .filter((p) => p.returnableQty > 0);
}, [returnableRaw, order]);

  const refundableServices = useMemo(() => {
    if (!order?.services) return [];
    return order.services
      .filter((s) => String(s?.status || "").toLowerCase().trim() !== "cancelled")
      .map((s) => ({
        ref_id: s.ref_id,
        serviceName: s.serviceName,
        type: s.type,
        price: Number(s.price || 0),
        petName: getPetInfo(s.petId)?.name || "",
      }));
  }, [order]);

  const productLookup = useMemo(() => {
    const map = {};
    (rawOrderData?.order_items || []).forEach((item) => {
      (item.products || []).forEach((p) => {
        map[`${item.ref_id}_${p._id || p.product_id}`] = {
          name: p.product_name || p.name || "Product",
          serviceName: item.service_name || "",
          petName: item.pet_name || "",
        };
      });
    });
    return map;
  }, [rawOrderData]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    toast.success("Form saved (demo)");
    // keep the panel open or close depending on desired UX
    // toggleClientPanel(null);
  };

    const INVOICE_ELIGIBLE_STATUSES = ["closed", "checkout", "checked_out", "completed"];

    const isInvoiceEligible = (service) => {
      const status = String(service?.status || "").toLowerCase().trim();
      const statusOk = INVOICE_ELIGIBLE_STATUSES.includes(status);
       const alreadySplit = Boolean(service?.invoiced || service?.invoice_id || service?.split);
      return statusOk && !alreadySplit;
    };

    const eligibleServiceCount = useMemo(
        () => order?.services?.filter(isInvoiceEligible).length || 0,
        [order]
      );
    const canSplit = eligibleServiceCount >= 1 && (order?.services?.length || 0) > 1;

    const isSplitOrder =
      rawOrderData?.order_category === "split" ||
      rawOrderData?.order_type === "split";

    const shouldHideInvoiceSplit = hideInvoiceSplit || isSplitOrder;

    const toggleInvoiceSelection = (refId) => {
      setSelectedForInvoice((prev) => {
        const next = new Set(prev);
        next.has(refId) ? next.delete(refId) : next.add(refId);
        return next;
      });
    };

  // Save logic
  const buildOrderPayload = (order) => {
    if (!order || !order.services?.length || !order.client?.client_id)
      return null;

    const items = order.services.map((service, idx) => {
      const originalItem = rawOrderData?.order_items?.find(
        (item) =>
          item._id === service.serviceId || item.ref_id === service.ref_id,
      );

      const isBoarding =
        service.type === "boarding" || service.type === "day_care";

      const item = {
        item_type: service.type,
        vendor_pet_id: service.petId,
        pet_name: originalItem?.pet_name || "",
        pet_type: originalItem?.pet_type || "",
        pet_type_id: originalItem?.pet_type_id,
        pet_size: originalItem?.pet_size,
        pet_size_id: originalItem?.pet_size_id,
        pet_breed: originalItem?.pet_breed,
        pet_breed_id: originalItem?.pet_breed_id,
        service_id: service.selectedServiceId || originalItem?.service_id,
        service_name: service.serviceName || "",
        technician_id: originalItem?.technician_id || "",
        employee_name: originalItem?.employee_name || "",
        start_date: service.checkin,
        start_time: originalItem?.start_time || "",
        duration: originalItem?.duration || "",
        unit_price: service.unit_price || service.price,
        price: service.price,
        tax_percentage: service.tax_percentage,
        gst: service.gst_amount,
        discounted_tax: service.gst_amount,
        total_price: service.price,
        notes: originalItem?.notes || "",
        addons: service.addons || [],
        products: service.products || [],
        otherCharges: service.otherCharges || [],
        service_based_billing:
          service.service_based_billing ||
          originalItem?.service_based_billing ||
          {},
        price_surcharge: service.price_surcharge || [],
        form_ids: originalItem?.form_ids || [],
        ref_id: originalItem?.ref_id || service.ref_id || "",
        status: originalItem?.status || "confirmed",
        quantity: service.quantity || 1,
      };

      if (isBoarding) {
        item.end_date = service.checkout;
        item.room_type_id = service.room_type_id || originalItem?.room_type_id;
        item.room_id = service.room_id || originalItem?.room_id;
        item.room_number = service.room_number || originalItem?.room_number;
        item.room_type_name = service.roomType || originalItem?.room_name;
        const duration =
          service.checkout && service.checkin
            ? Math.max(
                1,
                Math.ceil(
                  (new Date(service.checkout) - new Date(service.checkin)) /
                    86400000,
                ),
              )
            : 1;
        item.duration = duration;
        item.quantity = duration;
      } else if (service.type === "grooming") {
        item.service_category_type_id =
          service.service_category_type_id ||
          originalItem?.service_category_type_id ||
          "";
      } else if (service.type === "vet" || service.type === "vet_clinic") {
        item.vet_details = service.vet_details || originalItem?.vet_details || {};
      }

      item._editIndex = idx;
      return item;
    });

    return {
      order_type: rawOrderData?.order_type || "checkout",
      vendor_client_id: order.client.client_id,
      order_items: items,
      order_id: orderId,
    };
  };

  const [isClosing, setIsClosing] = useState(false);

  const handleClose = async () => {
    const result = await Swal.fire({
      title: "Close Order?",
      text: "This will close all services in this order. Continue?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Close All",
      cancelButtonText: "No",
    });
    if (!result.isConfirmed) return;

    setIsClosing(true);
    try {
      const orderResponse = await httpService.post(
        `/vendororder/closeOrder`,{orderId: orderId},
      );
      if (orderResponse.code === 200) {
        toast.success("Order Closed successfully.");
        await fetchOrder();
        setTimeout(onClose, 100);
      } else {
        setError("Failed to Close Order.");
      }
    } catch (err) {
      console.error("Error fetching order:", err);
      toast.error(err?.response?.data?.message || "Failed to close order.");
    } finally {
      setIsClosing(false);
    }
  };

  const handleSave = async (order) => {
    const payload = buildOrderPayload(order);
    if (!payload) {
      toast.error("Nothing to save");
      return;
    }

    setIsSaving(true);
    try {
      const res = await httpService.post(
        "/vendororder/addVendorOrderOrUpdate",
        payload,
      );
      if (res.code === 200 || res.status) {
        fetchOrder();
      } else {
        throw new Error(res.message || "Update failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateSplitInvoice = async () => {
  if (!selectedInvoiceTotals || selectedInvoiceTotals.items.length === 0) return;

  const result = await Swal.fire({
    title: "Split Order for Selected Items?",
    text: `This creates a separate invoice for ${selectedInvoiceTotals.items.length} item(s) totalling ₹${selectedInvoiceTotals.total.toFixed(0)}. Remaining items stay open on this order.`,
    icon: "question",
    showCancelButton: true,
    confirmButtonColor: "#4f46e5",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Yes, Split",
    cancelButtonText: "No",
  });
  if (!result.isConfirmed) return;

  // ignore a second click while a request is running
  if (splitInFlightRef.current) return;

  const refIds = selectedInvoiceTotals.items.map((s) => s.ref_id);

  // base payload without the key
  const basePayload = {
    order_id: orderId,
    order_category: "split",
    ref_ids: refIds,
  };

  // same payload = same key, changed payload = new key
  const fp = JSON.stringify(basePayload);
  if (splitIdemRef.current.fp !== fp) {
    splitIdemRef.current = { fp, key: crypto.randomUUID() };
  }

  splitInFlightRef.current = true;
  try {
    const res = await httpService.post("/vendororder/splitOrder", {
      ...basePayload,
      idempotencyKey: splitIdemRef.current.key,
    });

    if (res.code === 200 || res.status) {
      splitIdemRef.current = { fp: null, key: null };

      console.log("splitOrder response:", JSON.stringify(res.data, null, 2));
      toast.success("Order split and invoice generated successfully.");
      setSelectedForInvoice(new Set());
      fetchOrder();

      const newOrderId = res.data?.split_order_id || res.data?.order_id || res.data?._id;
      if (newOrderId) {
        setSplitOrderId(newOrderId);
        setShowSplitOrderDrawer(true);
      }
    } else {
      throw new Error(res.message || "Failed to split order");
    }
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to split order");
  } finally {
    splitInFlightRef.current = false;
  }
};

  const mapPrinterToUrlValue = (printer) => {
    console.log("🔍 mapPrinterToUrlValue received:", printer);
    if (!printer || typeof printer !== "string") return null;
    const p = printer.toLowerCase().trim();

    if (p === "thermal") return "thermal";
    if (["dotmatrix", "dot_matrix", "dot-matrix", "dot matrix"].includes(p))
      return "dotmatrix";
    if (["laser", "inkjet", "ink", "ink jet", "ink-jet"].includes(p))
      return "laser";
 if (["letterhead", "letter_head", "letter-head", "letter head"].includes(p))
   return "letterhead"; 
    return null;
  };

  const handleInvoiceClick = async (orderId) => {
    try {
       console.log("1. systemSettings from profile:", systemSettings);
      // Determine if it's a product-only order (POS)
      const isProductOnlyOrder = !order?.services || order.services.length === 0;


      const printerItems = systemSettings?.printer_by_item?.items || [];
      const targetName = isProductOnlyOrder ? "Invoice – POS" : "Invoice – Service";

      const invoiceConfig = printerItems.find(
        (item) =>
          item.name === targetName ||
          (item.name || "").toLowerCase() === targetName.toLowerCase(),
      );

      const selectedPrinter = invoiceConfig?.printer;
      const printerValue = mapPrinterToUrlValue(selectedPrinter);


      if (printerValue) {
        const baseUrl =
          process.env.REACT_APP_ADMIN_URL || "http://localhost:3000";
        const previewUrl = `${baseUrl}/admin/invoicePreview/${printerValue}/${orderId}`;
        window.open(previewUrl, "_blank", "noopener,noreferrer");
        return;
      }


      const vendorId = decodedData?.data?.vendor?._id || decodedData?.data?.user?.vendor_id;
      if (!vendorId) {
        toast.error("Vendor ID not found");
        return;
      }

      const invoiceRes = await httpService.get(
        `/invoice/getorderinvoice?orderId=${orderId}&vendorId=${vendorId}`,
      );
      if (invoiceRes.status) {
        window.open(
          invoiceRes.data.invoice_url,
          "_blank",
          "noopener,noreferrer",
        );
      }
    } catch (err) {
      console.error("Failed fetching invoice", err);
      toast.error("Failed to fetch invoice");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Render helpers ----------
  const renderClientHeader = () => (
    <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0 gap-2 bg-white">
      <div className="flex flex-col min-w-0 flex-1">
        <span className="font-bold text-lg">{order.client.name}</span>
        <span className="text-sm text-gray-600 flex items-center gap-1">
          <FaMapPin className="text-gray-400" /> {order.client.address}
        </span>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-sm text-gray-600 flex items-center gap-1">
            <FaPhone className="text-gray-400" /> {order.client.phone}
          </span>
          <span className="text-sm text-gray-600 flex items-center gap-1">
            <FaEnvelope className="text-gray-400" /> {order.client.email}
          </span>
        </div>
        <div className="flex flex-wrap gap-2 mb-2">
          <button
            className={`inline-flex items-center gap-2 text-xs font-semibold px-4 py-1.5 rounded-full transition ${
              activeClientPanel === "overview"
                ? "bg-indigo-100 text-indigo-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => toggleClientPanel("overview")}
          >
            <FaChartBar /> Overview
          </button>
          <button
            className={`inline-flex items-center gap-2 text-xs font-semibold px-4 py-1.5 rounded-full transition ${
              activeClientPanel === "recentVisits"
                ? "bg-indigo-100 text-indigo-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => toggleClientPanel("recentVisits")}
          >
            <FaHistory /> Recent Visits
          </button>
          <button
            className={`inline-flex items-center gap-2 text-xs font-semibold px-4 py-1.5 rounded-full transition ${activeClientPanel === "form" ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            onClick={() => toggleClientPanel("form")}
          >
            <FaClipboardList /> Form
          </button>
        </div>

        {activeClientPanel === "overview" && (
          <div className="mt-1 bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto">
            {loadingStats ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                <div className="bg-green-50 border border-green-200 p-3 rounded-lg shadow-sm flex flex-col items-center text-center">
                  <FaRupeeSign className="w-4 h-4 text-green-700 mb-1" />
                  <p className="text-gray-600 text-[11px] font-medium">Paid</p>
                  <p className="text-base font-bold text-gray-900 mt-0.5">
                    ₹{Number(calculateStats().paidAmount).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg shadow-sm flex flex-col items-center text-center">
                  <FaClock className="w-4 h-4 text-yellow-700 mb-1" />
                  <p className="text-gray-600 text-[11px] font-medium">Pending</p>
                  <p className="text-base font-bold text-gray-900 mt-0.5">
                    ₹{Number(calculateStats().pendingAmount).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg shadow-sm flex flex-col items-center text-center">
                  <FaReceipt className="w-4 h-4 text-blue-700 mb-1" />
                  <p className="text-gray-600 text-[11px] font-medium">Total Orders</p>
                  <p className="text-base font-bold text-gray-900 mt-0.5">
                    {calculateStats().totalOrders}
                  </p>
                </div>
                <div className="bg-red-50 border border-red-200 p-3 rounded-lg shadow-sm flex flex-col items-center text-center">
                  <FaCalendar className="w-4 h-4 text-red-700 mb-1" />
                  <p className="text-gray-600 text-[11px] font-medium">Cancellations</p>
                  <p className="text-base font-bold text-gray-900 mt-0.5">
                    {calculateStats().cancellations}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeClientPanel === "recentVisits" && (
          <div className="mt-1 bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto">
            {recentVisits?.length > 0 ? (
              <>
                {recentVisits?.map((visit, index) => (
                  <div
                    key={index}
                    className="rounded-xl overflow-hidden border border-gray-200"
                  >
                    <button
                      className="w-full bg-white border-b border-gray-200 px-2 py-1 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2 text-xs">
                        <span className="bg-white/20 px-2 py-0.5 rounded-md text-[11px] font-medium">
                          #{visit.order_number}
                        </span>
                        <span>
                          {new Date(visit.createdAt).toLocaleDateString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            },
                          )}
                        </span>
                        <span className="bg-green-300 px-2 py-0.5 rounded-md text-[11px] font-medium">
                          ₹
                          {Math.round(
                            visit.grand_total || visit.final_total,
                          )}
                        </span>
                        <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-md text-[11px]">
                          {visit.payment_status
                            ?.toLowerCase()
                            .replace(/\b\w/g, (char) => char.toUpperCase())}
                        </span>
                      </div>
                    </button>
                  </div>
                ))}
              </>
            ) : (
              <p className="text-sm text-gray-500">No recent visits found.</p>
            )}
          </div>
        )}

        {activeClientPanel === "form" && (
          <div className="mt-1 bg-gray-50 rounded-lg p-3 max-h-80 overflow-y-auto">
            {(() => {
              const firstService = order.services && order.services.length > 0 ? order.services[0] : null;
              const itemForForms = firstService
                ? {
                    ref_id: firstService.ref_id || firstService.serviceId || firstService.selectedServiceId || "",
                    _id: firstService._id || firstService.serviceId || firstService.selectedServiceId || "",
                    item_type: firstService.type || "",
                    vendor_pet_id: firstService.petId || "",
                    form_ids: firstService.form_ids || [],
                  }
                : { ref_id: orderId, _id: orderId };

              return (
                <OrderFormsSection
                  orderId={orderId}
                  item={itemForForms}
                  clientPhone={order.client?.phone || ""}
                  onMandatoryStatusChange={() => {}}
                  onFormIdsChange={() => {}}
                  hideBypassBlock={true}
                />
              );
            })()}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 flex-1">
        <div className="flex items-center justify-between">
          <span className="font-medium text-sm">#{order.orderNumber}</span>
        </div>

        {/* Payment summary row: Advance | Pending | Total */}
        <div className="mt-2 bg-gray-50 rounded-lg p-3">
          <div className="grid grid-cols-[1.4fr_1.4fr_1fr_auto] gap-4 items-center text-sm">
            <div>
              <span className="text-gray-400 text-xs">Advance Received</span>
              <div className="font-medium text-green-600 flex items-center">
                <FaRupeeSign className="w-3 h-3" />
                <span>{order.payment.advanceReceived}</span>
              </div>
            </div>
            <div>
              <span className="text-gray-400 text-xs">Pending</span>
              <div className="font-medium text-red-600 flex items-center">
                <FaRupeeSign className="w-3 h-3" />
                <span>{calculateTotals.balance.toFixed(0)}</span>
              </div>
            </div>
            <div>
              <span className="text-gray-400 text-xs">Total</span>
              <div className="font-medium text-blue-700 flex items-center">
                <FaRupeeSign className="w-3 h-3" />
                <span>{Number(order.payment.total).toFixed(0)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Adjustment card — placed BELOW the payment summary row */}
        <AdjustmentAmountCard
          adjustmentAmount={adjustmentAmount}
          adjustmentReason={adjustmentReason}
          onAdjustmentChange={setAdjustmentAmount}
          onAdjustmentReasonChange={setAdjustmentReason}
          orderTotal={parseFloat(order?.payment?.grandTotal || 0)}
          maxAmount={parseFloat(order?.payment?.grandTotal || 0)}
          showSaveButton
          onSave={handleSaveAdjustment}
          saving={savingAdjustment}
          savedValue={parseFloat(order?.payment?.adjustmentAmount || 0)}
          savedReason={order?.payment?.adjustmentReason || ""}
          disabled={isOrderClosed || isRecalculating || isSaving}
          className="mt-2"
        />
      </div>
      <button
        onClick={onClose}
        className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition text-gray-500 flex-shrink-0"
      >
        <FaTimes />
      </button>
    </div>
  );

  const renderBillingSidebar = () => (
    <div className="w-80 border-l border-gray-200 bg-white p-5 flex flex-col gap-4">
      <h3 className="font-bold text-gray-800 text-lg">Billing Summary</h3>
      <div className="bg-gray-50 rounded-xl p-4 space-y-2">
        {/* Sub Total */}
        {calculateTotals.discount > 0 && (
          <div className="flex justify-between text-sm">
            <span>Sub Total</span>
            <span>₹{calculateTotals.subtotal.toFixed(2)}</span>
          </div>
        )}

        {/* Discount */}
        {calculateTotals.discount > 0 && (
          <div className="flex justify-between text-sm text-red-500">
            <span>Discount</span>
            <span>-₹{calculateTotals.discount.toFixed(2)}</span>
          </div>
        )}

        {/* Net Amount = Amount without tax */}
        <div className="flex justify-between text-sm font-medium">
          <span>Net Amount</span>
          <span>₹{calculateTotals.net.toFixed(2)}</span>
        </div>

        {/* Taxes */}
        {calculateTotals.tax > 0 && (
          <div className="flex justify-between text-sm">
            <span>Taxes</span>
            <span>₹{calculateTotals.tax.toFixed(2)}</span>
          </div>
        )}

        <hr className="my-2" />
        {/* Adjustment (if non-zero) */}
        {calculateTotals.adjustment !== 0 && (
          <div className={`flex justify-between text-sm font-medium ${calculateTotals.adjustment > 0 ? 'text-blue-600' : 'text-red-500'}`}>
            <span>Adjustment</span>
            <span>
              {calculateTotals.adjustment > 0 ? '-' : '+'}₹{Math.abs(calculateTotals.adjustment).toFixed(2)}
            </span>
          </div>
        )}

        {/* Total Amount */}
        <div className="flex justify-between font-bold text-indigo-700">
          <span>Order Amount</span>
          <span>₹{Math.round(calculateTotals.finalTotal)}</span>   
        </div>       

        {/* Paid */}
        <div className="flex justify-between text-sm text-green-700">
          <span>Paid</span>
          <span>₹{Number(order.payment?.advanceReceived || 0).toFixed(2)}</span>
        </div>

        {/* Balance Due */}
        <div className="flex justify-between text-sm font-bold text-red-600">
          <span>Balance Due</span>
          <span>₹{Math.round(calculateTotals.balance)}</span>
        </div>
      </div>

       {selectedForInvoice.size > 0 && selectedInvoiceTotals && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 space-y-2">
          <h4 className="font-semibold text-indigo-800 text-sm">
            Split Invoice — {selectedInvoiceTotals.items.length} item(s) selected
          </h4>
          {selectedInvoiceTotals.items.map((s) => (
            <div key={s.ref_id} className="text-xs text-indigo-700">
              <div className="flex justify-between">
                <span>{s.serviceName}</span>
                <span>₹{Number(s.final_total ?? s.price).toFixed(0)}</span>
              </div>
              {(s.products?.length > 0) && (
                <div className="text-[11px] text-indigo-500">
                  incl. {s.products.length} product(s)
                </div>
              )}
            </div>
          ))}
          <hr className="border-indigo-200" />
          <div className="flex justify-between text-sm font-bold text-indigo-800">
            <span>Invoice Total</span>
            <span>₹{selectedInvoiceTotals.total.toFixed(0)}</span>
          </div>
          <button
            onClick={handleGenerateSplitInvoice}
            className="w-full bg-indigo-600 text-white py-2 rounded-lg font-semibold text-sm hover:bg-indigo-700 transition"
          >
            Split Order &amp; Generate Invoice
          </button>
          <p className="text-[11px] text-indigo-600">
            Remaining items stay open on this order until selected separately.
          </p>
        </div>
      )}

      <div className="mt-auto flex flex-col gap-2">
        <button
          onClick={() => handleInvoiceClick(orderId)}
          className="w-full border border-gray-300 bg-white py-2.5 rounded-xl font-semibold hover:bg-gray-100 transition flex items-center justify-center gap-2">
          <FaFileInvoice /> Print Invoice
        </button>
        {rawOrderData.total_paid_amount < Number(rawOrderData.amount_after_adjustment).toFixed(0)  && (
          <button
            onClick={() => setShowPaymentDrawer(true)}
            disabled={isRecalculating || isSaving}
            className={`w-full bg-green-600 text-white py-2.5 rounded-xl font-semibold hover:bg-green-700 transition flex items-center justify-center gap-2 ${
              isRecalculating || isSaving ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <FaCheckCircle />
            {isRecalculating
              ? "Calculating..."
              : isSaving
              ? "Saving..."
              : "Pay Now"}
          </button>
        )}
        {!isOrderClosed && (
          <>
          <button
            onClick={() => handleClose()}
            disabled={isRecalculating || isSaving || isClosing}
            className={`w-full bg-indigo-600 text-white py-2.5 rounded-xl font-semibold hover:bg-indigo-700 transition flex items-center justify-center gap-2 ${
              isRecalculating || isSaving || isClosing ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <IoBagCheckOutline />
            {isRecalculating
              ? "Calculating..."
              : isSaving
              ? "Saving..."
              : isClosing
              ? "Closing..."
              : "Close Order"}
          </button>
          {Number(order.payment.advanceReceived) > 0 && (
            <button
              onClick={() => setShowReturnDrawer(true)}
              className="w-full bg-rose-50 text-rose-700 border border-rose-200 py-2.5 rounded-xl font-semibold hover:bg-rose-100 transition flex items-center justify-center gap-2"
            >
              <FaUndo /> Refund
            </button>
          )}
        </>
        )}
        {isOrderClosed && (
          <>
            <div className="text-center text-xs text-slate-500 font-medium py-1">
              Order is closed — view only
              {Number(calculateTotals.balance) > 0 ? " (payment still allowed)" : ""}
            </div>
            {(returnableProducts.length > 0 || Number(order.payment.advanceReceived) > 0) && (
              <button
                onClick={() => setShowReturnDrawer(true)}
                className="w-full bg-rose-50 text-rose-700 border border-rose-200 py-2.5 rounded-xl font-semibold hover:bg-rose-100 transition flex items-center justify-center gap-2"
              >
                <FaUndo /> {Number(order.payment.advanceReceived) > 0 ? "Return / Refund" : "Return Products"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );

  const renderServiceCard = (service) => {
    const pet = getPetInfo(service.petId);
    const originalItem = rawOrderData?.order_items?.find(
      (item) => item.ref_id === service?.ref_id,
    );
    const empName =
      service.assignedStaff ||
      service.employee_name ||
      originalItem?.employee_name ||
      originalItem?.employee_details?.name ||
      originalItem?.appointment_info?.employee_details?.name ||
      "";
    const techId =
      service.technician_id ||
      originalItem?.technician_id ||
      originalItem?.employee_id ||
      originalItem?.appointment_info?.employee_id ||
      "";
    const sTime =
      service.startTime ||
      originalItem?.start_time ||
      originalItem?.time_slot ||
      originalItem?.appointment_info?.time_slot ||
      "";
    const dur =
      service.duration ||
      originalItem?.duration ||
      originalItem?.appointment_info?.duration ||
      "";

    const commonProps = {
      service: {
        ...service,
        assignedStaff: empName,
        employee_name: empName,
        technician_id: techId,
        startTime: sTime,
        duration: dur,
      },
      pet,
      getPetIcon,
      clientId: order?.client?.client_id,
    };

    switch (service.type) {
      case "boarding":
        return (
          <BoardingCheckoutCard
            key={service.ref_id}
            {...commonProps}
            onUpdate={(updatedService) =>
              handleUpdateService(service.ref_id, updatedService)
            }
            onCancel={() => handleCancelservice(service)}
            onViewActivity={handleOpenBoardingStarted}
            isOrderClosed={isOrderClosed}
          />
        );
      case "grooming":
        return (
          <GroomingCheckoutCard
            key={service.ref_id}
            {...commonProps}
            onUpdate={(updatedService) =>
              handleUpdateService(service.ref_id, updatedService)
            }
            onCancel={() => handleCancelservice(service)}
            onViewActivity={handleOpenGroomingStarted}
            isOrderClosed={isOrderClosed}
          />
        );
      case "vet":
      case "vet_clinic":
        return (
          <VetCheckoutCard
            key={service.ref_id}
            {...commonProps}
            onUpdate={(updatedService) =>
              handleUpdateService(service.ref_id, updatedService)
            }
            onCancel={() => handleCancelservice(service)}
            onViewActivity={handleOpenVetStarted}
            isOrderClosed={isOrderClosed}
          />
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <>
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
          onClick={onClose}
        />
        <div className="fixed right-0 top-0 h-full z-50 flex flex-col bg-slate-50 shadow-2xl w-full md:w-[90vw] lg:w-[90vw] xl:w-[85vw] 2xl:w-[80vw] items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-indigo-600"></div>
        </div>
      </>
    );
  }

  if (error || !order) {
    return (
      <>
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
          onClick={onClose}
        />
        <div className="fixed right-0 top-0 h-full z-50 flex flex-col bg-slate-50 shadow-2xl w-full md:w-[90vw] lg:w-[90vw] xl:w-[85vw] 2xl:w-[80vw] items-center justify-center">
          <p className="text-red-500 mb-4">{error || "Order not found"}</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      <div className="fixed right-0 top-0 h-full z-50 flex flex-col bg-slate-50 shadow-2xl w-full md:w-[90vw] lg:w-[90vw] xl:w-[85vw] 2xl:w-[80vw]">
        {/* Checkout / Closed status strip */}
        <div
          className={`w-full text-white text-center py-2 font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 ${
            isOrderClosed ? "bg-emerald-600" : "bg-indigo-600"
          }`}
        >
          <FaCheckCircle size={14} />
          {isOrderClosed ? "Closed" : "Check-Out"}
        </div>

        {renderClientHeader()}

        <div className="flex flex-1 overflow-hidden">
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="space-y-3">
                {order.services.map((service) => (
                  <div key={service.ref_id} className="relative">
                    {!shouldHideInvoiceSplit && canSplit && isInvoiceEligible(service) && !isOrderClosed && (
                      <label className="absolute -left-1 top-4 z-10">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          checked={selectedForInvoice.has(service.ref_id)}
                          onChange={() => toggleInvoiceSelection(service.ref_id)}
                          title="Select for split invoice"
                        />
                      </label>
                    )}
                    <div className={!shouldHideInvoiceSplit && isInvoiceEligible(service) ? "pl-6" : ""}>
                      {renderServiceCard(service)}
                    </div>
                  </div>
                ))}
              </div>
              <ReturnHistorySection entries={returnHistory} productLookup={productLookup}/>
            </div>
          </div>

          {renderBillingSidebar()}
        </div>
      </div>

      <OrderPaymentDrawer
        isOpen={showPaymentDrawer}
        onClose={() => setShowPaymentDrawer(false)}
        orderId={orderId}
        onPaymentSuccess={() => {
          setShowPaymentDrawer(false);
          fetchOrder();
        }}
      />

      <ReturnProductDrawer
        isOpen={showReturnDrawer}
        onClose={() => setShowReturnDrawer(false)}
        orderId={orderId}
        orderNumber={rawOrderData?.order_number}
        clientId={order.client.client_id}
        products={returnableProducts}
        balanceDue={Number(order.payment.advanceReceived) || 0}
        isOrderClosed={isOrderClosed}
        onReturnSuccess={() => {
          setShowReturnDrawer(false);
          fetchOrder();
        }}
      />

      {showSplitOrderDrawer && splitOrderId && (
        <OrderCheckoutDrawer
          isOpen={showSplitOrderDrawer}
          orderId={splitOrderId}
          onClose={() => {
            setShowSplitOrderDrawer(false);
            setSplitOrderId(null);
          }}
          hideInvoiceSplit={true}
        />
      )}

      {boardingStartedDrawerOpen && selectedBoardingBooking && (
        <BoardingStartedDrawer
          open={boardingStartedDrawerOpen}
          booking={selectedBoardingBooking}
          appointmentRow={selectedBoardingRow}
          onClose={handleCloseBoardingStarted}
        />
      )}
      {groomingStartedDrawerOpen && selectedGroomingBooking && (
        <GroomingStartedDrawer
          open={groomingStartedDrawerOpen}
          booking={selectedGroomingBooking}
          appointmentRow={selectedGroomingRow}
          onClose={handleCloseGroomingStarted}
          onSave={() => {
          }}
          onComplete={() => {
            fetchOrder();
          }}
        />
      )}
      {vetStartedDrawerOpen && selectedVetBooking && (
        <VetClinicStartedDrawer
          open={vetStartedDrawerOpen}
          booking={selectedVetBooking}
          appointmentRow={selectedVetRow}
          onClose={handleCloseVetStarted}
          onComplete={() => {
            fetchOrder();
          }}
        />
      )}
    </>
  );
};

export default OrderCheckoutDrawer;